import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { getFullImageUrl, handleImageError } from '../utils/imageUtils';
import PublicChrome from './public/PublicChrome';

interface Project {
  _id: string;
  title: string;
  description: string;
  projectUrl: string;
  slug: string;
  imageUrl?: string;
  apy?: string;
  commission?: string;
  minimumStake?: string;
  activationPeriod?: string;
  undelegatePeriod?: string;
  stakerAddress?: string;
  stakingLink?: string;
  ticker?: string;
  currentPrice?: number;
  lastPriceUpdate?: string;
  links?: {
    website?: string;
    github?: string;
    twitter?: string;
    discord?: string;
  };
  serviceBlocks?: {
    explorer?: { enabled?: boolean; title?: string; description?: string; link?: string };
    monitoring?: { enabled?: boolean; title?: string; description?: string; link?: string };
    validator?: { enabled?: boolean; title?: string; description?: string; link?: string };
    analytics?: { enabled?: boolean; title?: string; description?: string; link?: string };
  };
}

const StakePage: React.FC = () => {
  const { projectUrl } = useParams<{ projectUrl: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [stakeAmount, setStakeAmount] = useState<number>(10000);
  const [copied, setCopied] = useState(false);

  const getTokenPrice = () => project?.currentPrice || 2.5;
  const ticker = project?.ticker || project?.title?.split(' ').pop() || 'STRK';
  const apyNum = parseFloat(project?.apy || '11.7');

  const handleCopyAddress = async () => {
    if (project?.stakerAddress) {
      try {
        await navigator.clipboard.writeText(project.stakerAddress);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy: ', err);
      }
    }
  };

  useEffect(() => {
    const fetchProject = async () => {
      if (!projectUrl) return;
      try {
        const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
        const res = await axios.get(`${apiUrl}/api/projects?category=Stake`);
        if (res.data.success) {
          const projects = res.data.data.projects;
          const found = projects.find((p: Project) => {
            const urlPart = p.projectUrl ? p.projectUrl.split('/').pop() : p.title.toLowerCase().replace(/[^a-z0-9 -]/g, '').replace(/\s+/g, '-');
            return urlPart === projectUrl || p.slug === projectUrl || p.slug === `stake-${projectUrl}`;
          });
          if (found) setProject(found);
        }
      } catch (error) {
        console.error('Error fetching project:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProject();
  }, [projectUrl]);

  const sliderPct = Math.min((stakeAmount / 10000000) * 100, 100);

  if (loading) {
    return (
      <PublicChrome>
        <div className="flex items-center justify-center py-32">
          <div className="sp-spinner" />
        </div>
      </PublicChrome>
    );
  }

  if (!project) {
    return (
      <PublicChrome>
        <section className="sp-not-found">
          <h1 className="sp-not-found-title">Project Not Found</h1>
          <p className="sp-not-found-text">The stake project you are looking for does not exist.</p>
          <Link to="/stake" className="dusk-btn-primary">&larr; Back to Stake Projects</Link>
        </section>
      </PublicChrome>
    );
  }

  const renderServiceBlock = (
    block: { enabled?: boolean; title?: string; description?: string; link?: string } | undefined,
    defaultTitle: string,
    defaultDesc: string,
    imgSrc: string,
    imgAlt: string
  ) => {
    if (block?.enabled === false) return null;
    const inner = (
      <>
        <div className="sp-svc-img-wrap">
          <img src={imgSrc} alt={imgAlt} className="sp-svc-img" />
        </div>
        <h3 className="sp-svc-title">{block?.title || defaultTitle}</h3>
        <p className="sp-svc-desc">{block?.description || defaultDesc}</p>
        <svg className="sp-svc-arrow" width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
        </svg>
      </>
    );
    return (
      <div className="sp-svc-card">
        {block?.link ? (
          <a href={block.link} target="_blank" rel="noopener noreferrer" className="sp-svc-link">{inner}</a>
        ) : (
          <div className="sp-svc-link">{inner}</div>
        )}
      </div>
    );
  };

  return (
    <PublicChrome>
      {/* HERO BANNER */}
      <section className="sp-hero">
        <div className="sp-hero-inner">
          <div className="sp-hero-icon-row">
            {project.imageUrl ? (
              <img src={getFullImageUrl(project.imageUrl)} alt={project.title} className="sp-hero-icon" onError={handleImageError} />
            ) : (
              <div className="sp-hero-icon sp-hero-icon-placeholder">{project.title?.charAt(0) || 'S'}</div>
            )}
            <h1 className="sp-hero-title">{project.title}</h1>
          </div>

          <div className="sp-socials">
            {project.links?.website && (
              <a href={project.links.website} target="_blank" rel="noopener noreferrer" className="sp-social-btn" title="Website">
                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10A15.3 15.3 0 0112 2z"/></svg>
              </a>
            )}
            {project.links?.twitter && (
              <a href={project.links.twitter} target="_blank" rel="noopener noreferrer" className="sp-social-btn" title="X (Twitter)">
                <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              </a>
            )}
            {project.links?.github && (
              <a href={project.links.github} target="_blank" rel="noopener noreferrer" className="sp-social-btn" title="GitHub">
                <svg width="18" height="18" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 0C4.477 0 0 4.484 0 10.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0110 4.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.203 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.942.359.31.678.921.678 1.856 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0020 10.017C20 4.484 15.522 0 10 0z" clipRule="evenodd"/></svg>
              </a>
            )}
            {project.links?.discord && (
              <a href={project.links.discord} target="_blank" rel="noopener noreferrer" className="sp-social-btn" title="Discord">
                <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24"><path d="M20.317 4.37a19.791 19.791 0 00-4.885-1.515.074.074 0 00-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 00-5.487 0 12.64 12.64 0 00-.617-1.25.077.077 0 00-.079-.037A19.736 19.736 0 003.677 4.37a.07.07 0 00-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 00.031.057 19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 00-.041-.106 13.107 13.107 0 01-1.872-.892.077.077 0 01-.008-.128c.126-.094.252-.192.373-.292a.074.074 0 01.077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 01.078.01c.12.1.246.198.373.292a.077.077 0 01-.006.127 12.299 12.299 0 01-1.873.892.077.077 0 00-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.839 19.839 0 006.002-3.03.077.077 0 00.032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/></svg>
              </a>
            )}
          </div>

          <p className="sp-hero-desc">{project.description}</p>

          {project.stakerAddress && (
            <div className="sp-address-box">
              <code className="sp-address-code">{project.stakerAddress}</code>
              <button onClick={handleCopyAddress} className="sp-address-btn" title={copied ? 'Copied!' : 'Copy address'}>
                {copied ? (
                  <svg width="16" height="16" fill="none" stroke="#10b981" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                ) : (
                  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                )}
                {copied && <span className="sp-copied-label">Copied!</span>}
              </button>
            </div>
          )}

          <div className="sp-cta-row">
            {project.stakingLink ? (
              <a href={project.stakingLink} target="_blank" rel="noopener noreferrer" className="sp-btn-primary">
                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                Stake Now
              </a>
            ) : (
              <button className="sp-btn-primary sp-btn-disabled" disabled>
                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                Stake Now
              </button>
            )}
          </div>
        </div>
      </section>

      {/* CALCULATOR + RULES */}
      <section className="sp-section">
        <div className="sp-grid-2">
          <div className="sp-card">
            <h2 className="sp-card-heading">Calculate Your Earnings</h2>
            <div className="sp-apy-badge">~{project.apy || '11.7'}%<span className="sp-apy-label">APY</span></div>

            <div className="sp-calc-input-row">
              <input
                type="number"
                value={stakeAmount}
                onChange={(e) => setStakeAmount(Number(e.target.value))}
                className="sp-calc-input"
              />
              <span className="sp-calc-ticker">{ticker}</span>
            </div>

            <input
              type="range"
              min="1"
              max="10000000"
              value={stakeAmount}
              onChange={(e) => setStakeAmount(Number(e.target.value))}
              className="sp-slider"
              style={{
                background: `linear-gradient(to right, #6366f1 0%, #6366f1 ${sliderPct}%, #e5e7eb ${sliderPct}%, #e5e7eb 100%)`
              }}
            />
            <div className="sp-usd-equiv">&asymp; ${(stakeAmount * getTokenPrice()).toFixed(2)}</div>

            <div className="sp-earn-table">
              <div className="sp-earn-row">
                <span className="sp-earn-label">Daily</span>
                <span className="sp-earn-value">{(stakeAmount * (apyNum / 100) / 365).toFixed(2)} {ticker}</span>
                <span className="sp-earn-usd">&asymp; ${(stakeAmount * (apyNum / 100) / 365 * getTokenPrice()).toFixed(2)}</span>
              </div>
              <div className="sp-earn-row">
                <span className="sp-earn-label">Monthly</span>
                <span className="sp-earn-value">{(stakeAmount * (apyNum / 100) / 12).toFixed(2)} {ticker}</span>
                <span className="sp-earn-usd">&asymp; ${(stakeAmount * (apyNum / 100) / 12 * getTokenPrice()).toFixed(2)}</span>
              </div>
              <div className="sp-earn-row">
                <span className="sp-earn-label">Yearly</span>
                <span className="sp-earn-value">{(stakeAmount * (apyNum / 100)).toFixed(2)} {ticker}</span>
                <span className="sp-earn-usd">&asymp; ${(stakeAmount * (apyNum / 100) * getTokenPrice()).toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div className="sp-card">
            <h2 className="sp-card-heading">Staking Rules</h2>
            <div className="sp-rules-table">
              <div className="sp-rule-row"><span className="sp-rule-label">Ticker</span><span className="sp-rule-val">{ticker}</span></div>
              <div className="sp-rule-row"><span className="sp-rule-label">Commission</span><span className="sp-rule-val">{project.commission || '19'}%</span></div>
              <div className="sp-rule-row"><span className="sp-rule-label">APY</span><span className="sp-rule-val">~{project.apy || '11.7'}%</span></div>
              <div className="sp-rule-row"><span className="sp-rule-label">Minimum stake</span><span className="sp-rule-val">{project.minimumStake || '1'} {ticker}</span></div>
              <div className="sp-rule-row"><span className="sp-rule-label">Undelegate period</span><span className="sp-rule-val">{project.undelegatePeriod || '7'} days</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICE BLOCKS */}
      <section className="sp-section">
        <h2 className="sp-section-title">Our Services</h2>
        <div className="sp-svc-grid">
          {renderServiceBlock(
            project.serviceBlocks?.explorer,
            'Blockchain Explorer',
            'Explore with Precision. A next-gen explorer built for seamless navigation across networks.',
            '/images/dev/1.png',
            'Blockchain Explorer'
          )}
          {renderServiceBlock(
            project.serviceBlocks?.monitoring,
            'Alert System',
            'Stay Informed, Instantly. AI-enhanced Telegram bot that monitors your validator.',
            '/images/dev/2.svg',
            'Alert System'
          )}
          {renderServiceBlock(
            project.serviceBlocks?.validator,
            'Blockchain Services',
            'Full suite of infrastructure for mainnets and testnets including RPC, API, gRPC, snapshots, and state sync.',
            '/images/dev/3.png',
            'Blockchain Services'
          )}
          {renderServiceBlock(
            project.serviceBlocks?.analytics,
            'Analytics Dashboard',
            'Advanced analytics delivering insights into validator performance, uptime, and on-chain data.',
            '/images/dev/4.png',
            'Analytics Dashboard'
          )}
        </div>
      </section>

    </PublicChrome>
  );
};

export default StakePage;
